import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/PageHero";
import pageCover from "@/assets/kupish/p-najem.webp";

const FAQ: Array<[string, string]> = [
  ["Koliko stane najem spletne trgovine?", "Najem stane 240 € letno. Novo prijavljeni prodajalci imajo prve 3 mesece brezplačno, prodajalci, ki so bili prijavljeni pred uvedbo teh pogojev, pa 6 mesecev."],
  ["Kako deluje brezplačni preizkus?", "Ob registraciji trgovine se brezplačno obdobje aktivira samodejno — 3 mesece za nove prodajalce in 6 mesecev za obstoječe. Ves ta čas je trgovina javno objavljena in prodaja poteka običajno."],
  ["Kaj se zgodi po izteku brezplačnega obdobja?", "Za nadaljnjo objavo poravnate letno najemnino 240 €. Če najemnina ni plačana, se trgovina in izdelki umaknejo iz javne objave, vaši podatki in izdelki pa ostanejo shranjeni in se ob plačilu takoj znova objavijo."],
  ["Kaj vse je vključeno v najem?", "Najem trgovine, objava logotipa in profila prodajalca, kontaktni podatki, do 100 izdelkov, optimizacija spletnih vsebin in vzdrževanje."],
  ["Kdaj je trgovina objavljena?", "Takoj po registraciji in vnosu izdelkov — brezplačno obdobje se začne z registracijo, zato ni treba čakati na plačilo."],
  ["Zakaj je najem boljša rešitev kot lastna spletna trgovina?", "Ker je cenovno ugodnejši in brez skrbi za tehnično vzdrževanje, obenem pa izdelki dobijo obiskovalce nakupovalnega središča. Posvetite se lahko izključno prodaji."],
  ["Ali obstajajo skriti stroški?", "Ne. Poleg letne najemnine ne zaračunavamo nobenih dodatnih stroškov ali provizij od prodaje."],
  ["Kdo določa cene, poštnino in pogoje dostave?", "Prodajalec. Cene, zaloge, poštnino in pogoje dostave določate sami in jih kadar koli uredite v nadzorni plošči."],
  ["Kako prejmem naročilo?", "Vsako naročilo prejmete na svoj e-naslov in v nadzorno ploščo. Kupcu izdate račun oziroma predračun ter odpošljete izdelke."],
  ["Kdo skrbi za zalogo in opise izdelkov?", "Prodajalec — redno posodabljanje zaloge, dodajanje novih izdelkov in točnost opisov ter cen."],
  ["Kdo odgovarja za garancijo in reklamacije?", "Prodajalec. kupish.si upravlja nakupovalno središče in tehnično platformo ter posreduje naročila in povpraševanja."],
  ["Kaj potrebujem za začetek?", "Registrirano dejavnost ter opise in fotografije izdelkov. Plačilo najemnine je potrebno šele po izteku brezplačnega obdobja."],
  ["Je sistem primeren za začetnike?", "Da. Urejanje izdelkov je enostavno in prilagojeno tudi tistim brez tehničnih izkušenj."],
];

const TITLE = "Najem spletne trgovine — 3 mesece brezplačno, nato 240 € letno | kupish.si";
const DESC =
  "Najem spletne trgovine v nakupovalnem središču kupish.si: novi prodajalci 3 mesece brezplačno, nato 240 € letno, do 100 izdelkov, brez provizij in skritih stroškov.";



export const Route = createFileRoute("/najem-spletne-trgovine")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://kupish.si/najem-spletne-trgovine" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "sl_SI" },
      { property: "og:site_name", content: "kupish.si" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: "https://kupish.si/najem-spletne-trgovine" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Najem spletne trgovine",
          serviceType: "Najem spletne trgovine v spletnem nakupovalnem središču",
          areaServed: "SI",
          provider: { "@type": "Organization", name: "kupish.si", url: "https://kupish.si" },
          offers: { "@type": "Offer", price: "240", priceCurrency: "EUR", url: "https://kupish.si/najem-spletne-trgovine" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          url: "https://kupish.si/najem-spletne-trgovine",
          inLanguage: "sl-SI",
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "[data-answer]"] },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <article className="pb-24">
      <PageHero src={pageCover} alt="Najem spletne trgovine na kupish.si — minimalističen 3D prikaz spletne trgovine">
        <div className="eyebrow">Za ponudnike</div>
        <h1 className="font-serif text-5xl md:text-6xl mt-3 leading-tight">
          Najem spletne <span className=" text-accent">trgovine</span>
        </h1>
        <p data-answer className="mt-6 text-lg leading-relaxed max-w-3xl">
          <strong>Na kratko:</strong> novo prijavljeni prodajalci imajo <strong>prve 3 mesece
          brezplačno</strong>, prodajalci, ki so bili prijavljeni pred uvedbo teh pogojev, pa
          <strong> 6 mesecev</strong>. Po izteku brezplačnega obdobja najem stane <strong>240 €
          letno</strong> in vključuje objavo do 100 izdelkov, profil prodajalca, gostovanje,
          vzdrževanje in promocijo nakupovalnega središča. Provizije od prodaje ne zaračunavamo,
          skritih stroškov ni.
        </p>
        <p className="mt-4 text-lg text-muted-foreground max-w-3xl">
          Namesto postavljanja in vzdrževanja lastne spletne trgovine dobite pripravljeno
          prodajno mesto v nakupovalnem središču, kjer kupci že iščejo izdelke.
        </p>
      </PageHero>
      <div className="mx-auto max-w-3xl px-6">

      <div className="mt-10 rounded-lg border border-border bg-muted/30 p-6 space-y-2 text-sm">
        <p className="font-semibold text-base">Ponudnik storitve</p>
        <p>ARTEPIN, spletne rešitve, d.o.o.</p>
        <p>Šišenska cesta 17, 1000 Ljubljana</p>
        <p>IBAN: SI56 6100 0002 3812 295</p>
      </div>

      <div className="mt-10 rounded-lg border-2 border-accent p-8 text-center">
        <p className="eyebrow text-accent">Spletna trgovina</p>
        <p className="font-serif text-4xl mt-2 text-accent">3 mesece brezplačno</p>
        <p className="mt-2 text-sm text-foreground">nato 240 € / leto — brez provizij od prodaje</p>
        <ul className="mt-6 text-left text-sm space-y-1 text-muted-foreground max-w-sm mx-auto">
          <li>• najem spletne trgovine</li>
          <li>• objava logotipa in profila prodajalca</li>
          <li>• objava kontaktnih podatkov</li>
          <li>• objava do 100 izdelkov</li>
          <li>• optimizacija spletnih vsebin</li>
          <li>• vzdrževanje spletne trgovine</li>
        </ul>
        <Link to="/placilo-najemnine" className="mt-8 inline-block px-8 py-3 bg-accent text-accent-foreground rounded font-medium">
          Plačaj najemnino
        </Link>
        <p className="mt-3 text-xs text-muted-foreground">
          Plačilo je potrebno šele po izteku brezplačnega obdobja.
        </p>
      </div>

      <h2 className="font-serif text-3xl mt-16">Kako poteka</h2>
      <ol className="mt-6 space-y-4 text-muted-foreground list-decimal list-inside">
        <li><strong className="text-foreground">Registracija:</strong> ustvarite račun in izpolnite profil trgovine — ime, logotip, opis in kontaktne podatke.</li>
        <li><strong className="text-foreground">Brezplačno obdobje:</strong> trgovina je javno objavljena takoj — novi prodajalci 3 mesece brezplačno, obstoječi 6 mesecev.</li>
        <li><strong className="text-foreground">Objava izdelkov:</strong> dodate izdelke z opisi, fotografijami, cenami in zalogo. Uvrstite jih v ustrezne kategorije nakupovalnega središča.</li>
        <li><strong className="text-foreground">Prodaja:</strong> naročila prejemate po e-pošti in v nadzorno ploščo, izdelke odpošljete in izdate račun.</li>
        <li><strong className="text-foreground">Podaljšanje:</strong> po izteku brezplačnega obdobja poravnate letno najemnino 240 € in trgovina ostane objavljena.</li>
      </ol>


      <h2 className="font-serif text-3xl mt-16">Obveznosti prodajalca</h2>
      <ul className="mt-6 space-y-4 text-muted-foreground">
        <li><strong className="text-foreground">Ažurnost ponudbe:</strong> cene, zaloge in opisi morajo ustrezati dejanskemu stanju.</li>
        <li><strong className="text-foreground">Odprema naročil:</strong> naročila obdelate v dogovorjenem roku in kupca obvestite o oddaji pošiljke.</li>
        <li><strong className="text-foreground">Zakonodaja:</strong> kot prodajalec odgovarjate za račun, garancijo, vračila in reklamacije v skladu s slovensko potrošniško zakonodajo.</li>
        <li><strong className="text-foreground">Novi izdelki:</strong> v nakupovalnem središču se prodajajo novi izdelki, ne rabljeni.</li>
      </ul>

      <p className="mt-10 text-muted-foreground">
        Podrobna navodila za delo z nadzorno ploščo najdete v{" "}
        <Link to="/uporabniska-navodila-ustvarjalci" className="text-accent underline underline-offset-4">
          navodilih za prodajalce
        </Link>
        .
      </p>


      <h2 className="font-serif text-3xl mt-16">Pogosta vprašanja</h2>
      <div className="mt-6 space-y-6">
        {FAQ.map(([q, a]) => (
          <div key={q}>
            <p className="font-semibold">{q}</p>
            <p className="mt-1 text-muted-foreground">{a}</p>
          </div>
        ))}
      </div>
      </div>
    </article>
  );
}
